import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';
import Icon from 'components/AppIcon';

const LoginFormCard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams?.get('returnTo') || '/landing-page';
  const messageParam = searchParams?.get('message');
  const auth = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    if (messageParam) {
      setErrors({ submit: messageParam });
    }
  }, [messageParam]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.email) {
      newErrors.email = "L'email è obbligatoria";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Inserisci un indirizzo email valido';
    }
    if (!formData?.password) {
      newErrors.password = 'La password è obbligatoria';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'La password deve contenere almeno 6 caratteri';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const { data, error } = await auth?.signIn(formData?.email, formData?.password);
      if (error) {
        console.error('❌ Errore di accesso:', error);
        if (error?.message?.includes('Invalid login credentials')) {
          setErrors({ submit: 'Email o password non corretti. Verifica le tue credenziali e riprova.' });
        } else if (error?.message?.includes('Email not confirmed')) {
          setErrors({ submit: 'Devi confermare la tua email prima di accedere. Controlla la tua casella di posta.' });
        } else if (error?.message?.includes('fetch') || error?.message?.includes('Network')) {
          setErrors({ submit: 'Errore di connessione. Verifica la tua connessione internet e riprova.' });
        } else {
          setErrors({ submit: error?.message || "Si è verificato un errore durante l'accesso. Riprova." });
        }
        setIsLoading(false);
        return;
      }

      const userId = data?.user?.id;
      if (userId) {
        const { data: userData, error: statusError } = await supabase
          ?.from('users')
          ?.select('account_status, role')
          ?.eq('id', userId)
          ?.maybeSingle();
        if (!statusError && userData) {
          const status = userData?.account_status;
          if (status === 'suspended') {
            await supabase?.auth?.signOut();
            setErrors({ submit: 'Il tuo account è temporaneamente sospeso. Per assistenza contatta volapticpro@gmail.com' });
            setIsLoading(false);
            return;
          }
          if (status === 'banned') {
            await supabase?.auth?.signOut();
            setErrors({ submit: 'Il tuo account è stato disattivato. Per assistenza contatta volapticpro@gmail.com' });
            setIsLoading(false);
            return;
          }

          const userRole = userData?.role;
          if (userRole === 'superadmin' || userRole === 'admin') {
            navigate('/admin', { replace: true });
          } else if (userRole === 'cliente') {
            navigate('/cliente/dashboard', { replace: true });
          } else {
            navigate('/auth/callback', { replace: true });
          }
          return;
        }

        // users query failed or returned no row — fall back to /auth/callback
        console.error('⚠️ Could not read role from users table, falling back to /auth/callback', statusError);
        navigate('/auth/callback', { replace: true });
        return;
      }

      // No userId — fall back to /auth/callback
      navigate('/auth/callback', { replace: true });
    } catch (error) {
      console.error('❌ Errore imprevisto:', error);
      setErrors({ submit: 'Si è verificato un errore imprevisto. Riprova più tardi.' });
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '440px',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.05)',
        padding: '40px',
      }}
      className="login-card-responsive"
    >
      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <img
          src="/assets/images/logo-1775050470890.png"
          alt="Volaptic logo"
          style={{ height: '48px', width: 'auto', display: 'inline-block' }}
        />
      </div>
      {/* Heading */}
      <h1
        style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#111111',
          textAlign: 'center',
          marginBottom: '28px',
          fontFamily: 'inherit',
          lineHeight: '1.2',
        }}
      >
        Bentornato
      </h1>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Email field */}
        <div>
          <label
            htmlFor="login-email"
            style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}
          >
            Email
          </label>
          <input
            id="login-email"
            type="email"
            name="email"
            placeholder="tuo@email.it"
            value={formData?.email}
            onChange={handleChange}
            autoFocus
            disabled={isLoading}
            required
            style={{
              width: '100%',
              height: '48px',
              borderRadius: '10px',
              border: errors?.email ? '1px solid #DC2626' : '1px solid #d1d5db',
              padding: '0 14px',
              fontSize: '16px',
              color: '#111827',
              outline: 'none',
              boxSizing: 'border-box',
              backgroundColor: '#fff',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => { if (!errors?.email) e.target.style.borderColor = '#2780b5'; }}
            onBlur={(e) => { if (!errors?.email) e.target.style.borderColor = '#d1d5db'; }}
          />
          {errors?.email && (
            <p style={{ fontSize: '13px', color: '#DC2626', marginTop: '4px' }}>{errors?.email}</p>
          )}
        </div>

        {/* Password field */}
        <div>
          <label
            htmlFor="login-password"
            style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}
          >
            Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder="Inserisci la tua password"
              value={formData?.password}
              onChange={handleChange}
              disabled={isLoading}
              required
              style={{
                width: '100%',
                height: '48px',
                borderRadius: '10px',
                border: errors?.password ? '1px solid #DC2626' : '1px solid #d1d5db',
                padding: '0 44px 0 14px',
                fontSize: '16px',
                color: '#111827',
                outline: 'none',
                boxSizing: 'border-box',
                backgroundColor: '#fff',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => { if (!errors?.password) e.target.style.borderColor = '#2780b5'; }}
              onBlur={(e) => { if (!errors?.password) e.target.style.borderColor = '#d1d5db'; }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isLoading}
              aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#6b7280',
                display: 'flex',
                alignItems: 'center',
                padding: '0',
              }}
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} strokeWidth={2} />
            </button>
          </div>
          {errors?.password && (
            <p style={{ fontSize: '13px', color: '#DC2626', marginTop: '4px' }}>{errors?.password}</p>
          )}
        </div>

        {/* Error message */}
        {errors?.submit && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: 'rgba(220,38,38,0.08)',
              border: '1px solid rgba(220,38,38,0.2)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
            }}
          >
            <Icon name="AlertCircle" size={18} color="#DC2626" strokeWidth={2} />
            <p style={{ fontSize: '13px', color: '#DC2626', margin: 0, flex: 1 }}>{errors?.submit}</p>
          </div>
        )}

        {/* Remember me + forgot password */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e?.target?.checked)}
              disabled={isLoading}
              style={{ width: '16px', height: '16px', accentColor: '#2780b5', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '13px', color: '#6b7280' }}>Ricordami</span>
          </label>
          <button
            type="button"
            onClick={() => navigate('/reset-password')}
            disabled={isLoading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '13px',
              color: '#6b7280',
              cursor: 'pointer',
              padding: '0',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => (e.target.style.color = '#2780b5')}
            onMouseLeave={(e) => (e.target.style.color = '#6b7280')}
          >
            Hai dimenticato la password?
          </button>
        </div>

        {/* CTA Button */}
        <button
          type="submit"
          disabled={isLoading}
          style={{
            width: '100%',
            height: '48px',
            borderRadius: '10px',
            backgroundColor: isLoading ? '#6aabcf' : '#2780b5',
            color: '#ffffff',
            fontWeight: '700',
            fontSize: '15px',
            border: 'none',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'background-color 0.2s',
            marginTop: '4px',
          }}
          onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = '#104d98'; }}
          onMouseLeave={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = '#2780b5'; }}
        >
          {isLoading ? (
            <>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              Accesso in corso...
            </>
          ) : (
            'Accedi →'
          )}
        </button>
      </form>
      {/* Divider */}
      <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '24px 0' }} />
      {/* Registration links */}
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Non hai un account?</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <Link
            to="/signup-form"
            style={{ fontSize: '13px', color: '#2780b5', textDecoration: 'none', fontWeight: '500' }}
            onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
            onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
          >
            Registrati come professionista
          </Link>
          <span style={{ color: '#9ca3af', fontSize: '13px' }}>|</span>
          <Link
            to="/registrati-cliente"
            style={{ fontSize: '13px', color: '#2780b5', textDecoration: 'none', fontWeight: '500' }}
            onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
            onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
          >
            Registrati come cliente
          </Link>
        </div>
      </div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 640px) {
          .login-card-responsive {
            padding: 24px !important;
            width: 90% !important;
          }
        }
      `}</style>
    </div>
  );
};

export default LoginFormCard;